import type { AxialCoord } from "../hex";
import type { RunRules, UnitStats } from "../types/rules";
import type { FactionId, RunState, Unit, UnitKind } from "../types/state";

/**
 * One unit's record: its kind's stats, a new unit as a yard or the human's seat makes it, and the
 * one-unit writes (add, replace, update every unit). The key order of `newUnit` is the run's
 * bytes (the state hash is JSON), so every unit is born through it.
 */

export function unitStats(rules: RunRules, kind: UnitKind): UnitStats {
  return rules.units[kind];
}

/** A unit fresh from a yard or a spawn: the next id, full health, still and unordered since now. */
export function newUnit(
  state: RunState,
  kind: UnitKind,
  owner: FactionId,
  coord: AxialCoord,
): Unit {
  return {
    id: state.nextUnitId,
    kind,
    owner,
    coord,
    hp: unitStats(state.rules, kind).hp,
    movedTick: state.tick,
    orderedTick: state.tick,
    commander: false,
    kills: 0,
  };
}

/** The run with `unit` on the map, last in id order, and the next id moved on. */
export function addUnit(state: RunState, unit: Unit): RunState {
  return { ...state, units: [...state.units, unit], nextUnitId: state.nextUnitId + 1 };
}

/** The run with `unit` (by identity) replaced by `replacement`. */
export function replaceUnit(state: RunState, unit: Unit, replacement: Unit): RunState {
  return {
    ...state,
    units: state.units.map((candidate) => (candidate === unit ? replacement : candidate)),
  };
}

/** The unit has neither moved nor been given an order for `ticks`: a delegate may send it now. */
export function restedFor(state: RunState, unit: Unit, ticks: number): boolean {
  return state.tick - Math.max(unit.movedTick, unit.orderedTick) >= ticks;
}

/** Every unit through `change`; the same run back when `change` returned every unit as it was. */
export function updateUnits(state: RunState, change: (unit: Unit) => Unit): RunState {
  let changed = false;
  const units = state.units.map((unit) => {
    const next = change(unit);
    changed ||= next !== unit;
    return next;
  });
  return changed ? { ...state, units } : state;
}
